/**
 * WishlistAdmin — Gestión del botón CTA (wishlist / cumpleaños / ninguno) por geo
 *
 * Permite configurar para cada provincia de Argentina (y escalable a países)
 * si se muestra el botón de wishlist, el de cumpleaños, o ninguno.
 *
 * La configuración se guarda en la tabla `wishlist_geo_config` (PostgreSQL)
 * y el Cloudflare Worker la consume vía /api/wishlist-config.
 */

import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import {
  CTA_REGION_EVENTS,
  trackCtaRegionEvent,
} from "@/lib/geoSim/analytics";
import { GEO_SIM_SCENARIO_PRESETS } from "@/lib/geoSim/constants";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { GeoSimCountryPreviewButton } from "@/components/admin/GeoSimCountryPreviewButton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { broadcastSiteSettingsUpdated } from "@/lib/siteSettingsSync";
import {
  AR_CITY_ALIAS_TO_CANONICAL,
  AR_CITY_CATALOG,
  AR_CITY_CATALOG_KEYS,
  AR_PROVINCES,
  INTERNATIONAL_COUNTRIES,
  normalizeCityKey,
  wishlistCityLookupKeys,
  type GeoCtaType,
} from "@/config/wishlistGeoCatalog";
import {
  alignCityCatalogToProvinces,
  buildWishlistGeoDirtyDiff,
  buildWishlistGeoPersistEntries,
  countCityCatalogProvinceMismatches,
  hydrateCityCatalogFromDb,
  cascadeProvinceCtaToCityCatalog,
  snapshotWishlistBaseline,
  type WishlistGeoBaseline,
} from "@/config/wishlistGeoPersistence";
import {
  buildProvinceCityGroups,
  type ProvinceCityGroup,
} from "@/config/wishlistCityCatalogUi";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Ban, CheckCircle2, Info, Loader2, Save, Globe, MapPin, Zap, Sparkles, Building2, Plus, Trash2, AlertTriangle, ListOrdered, BarChart3, ChevronDown, RefreshCw } from "lucide-react";
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type CtaType = GeoCtaType;
type GeoScope = "province_ar" | "country" | "city_ar";

interface WishlistGeoEntry {
  id?: number;
  scope: GeoScope;
  geo_code: string;
  cta_type: CtaType;
  label?: string | null;
  url?: string | null;
  enabled: boolean;
}

/** Fila UI para scope city_ar (cf.city de Cloudflare, matching normalizado en el Worker). */
interface CityArRow {
  key: string;
  geo_code: string;
  cta_type: CtaType;
}

interface GeoTrafficProvinceRow {
  province: string | null;
  province_code: string | null;
  sessions: number;
  pct: number;
}

interface GeoTrafficCityRow {
  city: string;
  city_key: string;
  province: string | null;
  province_code: string | null;
  sessions: number;
  pct: number;
}

interface GeoTrafficPayload {
  days: number;
  hours?: number | null;
  window?: string;
  as_of?: string;
  total_ar_sessions: number;
  provinces: GeoTrafficProvinceRow[];
  cities: GeoTrafficCityRow[];
}

/**
 * Nivel visual de tráfico (7d fijo) — el admin decide por color, no por ventanas.
 * none=gris · low=naranja · mid=ámbar · high=verde · hot=esmeralda (vale Exclusivo).
 */
type TrafficHeat = "none" | "low" | "mid" | "high" | "hot";

function trafficHeatLevel(sessions: number, totalSessions: number): TrafficHeat {
  if (!sessions || sessions <= 0) return "none";
  const pct = totalSessions > 0 ? (sessions / totalSessions) * 100 : 0;
  if (sessions >= 40 || pct >= 4) return "hot";
  if (sessions >= 12 || pct >= 1.5) return "high";
  if (sessions >= 4 || pct >= 0.4) return "mid";
  return "low";
}

const TRAFFIC_HEAT_META: Record<
  TrafficHeat,
  { label: string; hint: string; dot: string; ring: string }
> = {
  none: {
    label: "Sin tráfico",
    hint: "Nadie entró desde acá · no prioritario",
    dot: "bg-zinc-300 dark:bg-zinc-600",
    ring: "ring-zinc-300/50 dark:ring-zinc-600/40",
  },
  low: {
    label: "Poco",
    hint: "Tráfico bajo · opcional",
    dot: "bg-orange-400",
    ring: "ring-orange-400/40",
  },
  mid: {
    label: "Medio",
    hint: "Algo de gente · evaluar",
    dot: "bg-amber-400",
    ring: "ring-amber-400/50",
  },
  high: {
    label: "Alto",
    hint: "Vale prender Exclusivo Mux",
    dot: "bg-emerald-500",
    ring: "ring-emerald-500/50",
  },
  hot: {
    label: "Muy alto",
    hint: "Prioridad: Exclusivo Mux acá",
    dot: "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]",
    ring: "ring-emerald-400/70",
  },
};

/** Icono de color para el admin (sin mirar números de ventana). */
function TrafficHeatIcon({
  sessions,
  totalSessions,
  size = "md",
}: {
  sessions: number;
  totalSessions: number;
  size?: "sm" | "md";
}) {
  const level = trafficHeatLevel(sessions, totalSessions);
  const meta = TRAFFIC_HEAT_META[level];
  const dim = size === "sm" ? "h-2.5 w-2.5" : "h-3.5 w-3.5";
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full ring-2 ${meta.ring}`}
      title={`${meta.label}: ${sessions.toLocaleString("es-AR")} sess · ${meta.hint}`}
      aria-label={`${meta.label}, ${sessions} sesiones`}
    >
      <span className={`block rounded-full ${dim} ${meta.dot}`} />
    </span>
  );
}

// Catálogo estático (AR provinces, CIUDADES catalog, país list, normalizeCityKey): @/config/wishlistGeoCatalog

/** Filtro de la pestaña Lista unificada. */
type UnifiedScopeFilter =
  | "all"
  | "country"
  | "province"
  | "city_cat"
  | "city_adhoc";

/** Una fila de la tabla “Lista” para actualizar cualquier geo CTA desde un solo lugar. */
type UnifiedCtaRow =
  | { id: string; bucket: "country"; code: string; label: string }
  | { id: string; bucket: "province"; code: string; label: string; region: string }
  | {
      id: string;
      bucket: "city_cat";
      canonical: string;
      label: string;
      region: string;
      provinceCode: string;
    }
  | {
      id: string;
      bucket: "city_alias";
      canonical: string;
      aliasCode: string;
      label: string;
      parentLabel: string;
      region: string;
      provinceCode: string;
    }
  | { id: string; bucket: "city_adhoc"; rowKey: string; geo_code: string };

function unifiedRowSearchBlob(row: UnifiedCtaRow): string {
  switch (row.bucket) {
    case "country":
      return `${row.label} ${row.code}`;
    case "province":
      return `${row.label} ${row.code} ${row.region}`;
    case "city_cat": {
      const pn =
        AR_PROVINCES.find((p) => p.code === row.provinceCode)?.name ?? row.provinceCode;
      return `${row.label} ${row.canonical} ${row.region} ${pn}`;
    }
    case "city_alias": {
      const pn =
        AR_PROVINCES.find((p) => p.code === row.provinceCode)?.name ?? row.provinceCode;
      return `${row.label} ${row.aliasCode} ${row.parentLabel} ${row.canonical} ${row.region} ${pn}`;
    }
    case "city_adhoc":
      return row.geo_code;
  }
}

const DEFAULT_AR_CTA: CtaType = "none";
const DEFAULT_COUNTRY_CTA: CtaType = "none";

/** Único CTA activo: Exclusivo Mux → /invitacion-exclusive. Resto deshabilitado. */
const ACTIVE_CTA_TYPES = ["none", "community"] as const;
type ActiveCtaType = (typeof ACTIVE_CTA_TYPES)[number];

function isActiveCta(t: string): t is ActiveCtaType {
  return t === "none" || t === "community";
}

/** birthday / wishlist / dual_video → none (no se ofrecen en admin ni se re-persisten). */
function sanitizeCta(t: CtaType | string | null | undefined): ActiveCtaType {
  return t === "community" ? "community" : "none";
}

const PRIORITY_COUNTRIES = [
  "US",
  "MX",
  "ES",
  "BR",
  "GB",
  "DE",
  "FR",
  "CO",
  "CL",
] as const;

type CountryListFilter = "priority" | "configured" | "all";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function CtaBadge({ type }: { type: CtaType }) {
  if (type === "community") {
    return (
      <Badge variant="secondary" className="gap-1 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-400">
        <Sparkles className="h-3 w-3" /> Exclusivo Mux
      </Badge>
    );
  }
  if (type === "birthday" || type === "wishlist" || type === "dual_video") {
    return (
      <Badge variant="outline" className="gap-1 text-muted-foreground line-through opacity-70">
        <Ban className="h-3 w-3" /> Deshabilitado
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 text-muted-foreground">
      <Ban className="h-3 w-3" /> Sin botón
    </Badge>
  );
}

function WishlistCtaPicker({
  value,
  onChange,
  triggerClassName,
}: {
  value: CtaType;
  onChange: (v: CtaType) => void;
  triggerClassName?: string;
}) {
  const safeValue: ActiveCtaType = isActiveCta(value) ? value : "none";
  return (
    <Select value={safeValue} onValueChange={(v) => onChange(sanitizeCta(v))}>
      <SelectTrigger className={`w-[200px] shrink-0 ${triggerClassName ?? ""}`}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">
          <span className="flex items-center gap-2">
            <Ban className="h-3.5 w-3.5 text-muted-foreground" />
            Sin botón
          </span>
        </SelectItem>
        <SelectItem value="community">
          <span className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-purple-500" />
            Exclusivo Mux (/invitacion-exclusive)
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}

/** Indicador visual de tráfico (color) + sess opcional. */
function GeoTrafficBar({
  sessions,
  pct,
  totalSessions,
  showZero = false,
}: {
  sessions: number;
  pct: number;
  totalSessions: number;
  showZero?: boolean;
}) {
  if (sessions <= 0 && !showZero) return null;
  const level = trafficHeatLevel(sessions, totalSessions);
  const meta = TRAFFIC_HEAT_META[level];
  return (
    <div
      className="flex shrink-0 items-center gap-1.5"
      title={`${meta.label}: ${sessions.toLocaleString("es-AR")} sess · ${pct.toFixed(1)}% · ${meta.hint}`}
    >
      <TrafficHeatIcon sessions={sessions} totalSessions={totalSessions} />
      {sessions > 0 ? (
        <span className="whitespace-nowrap text-[10px] font-semibold tabular-nums text-foreground/80">
          {sessions.toLocaleString("es-AR")}
        </span>
      ) : (
        <span className="whitespace-nowrap text-[10px] tabular-nums text-muted-foreground/60">
          —
        </span>
      )}
    </div>
  );
}

function CatalogCityEditorRow({
  c,
  current,
  provincialCta,
  traffic,
  totalSessions,
  provinceLabelText,
  onCtaChange,
}: {
  c: (typeof AR_CITY_CATALOG)[number];
  current: CtaType;
  provincialCta: CtaType;
  traffic: { sessions: number; pct: number } | null;
  totalSessions: number;
  provinceLabelText: string;
  onCtaChange: (v: CtaType) => void;
}) {
  const provinceMismatch = current !== provincialCta;
  return (
    <div className="space-y-1.5">
      <div
        className={`flex items-center justify-between gap-4 rounded-lg border px-3 py-2.5 ${
          provinceMismatch
            ? "border-amber-200/90 bg-amber-50/35 dark:border-amber-900/70 dark:bg-amber-950/20"
            : "border-border/50 bg-muted/20"
        }`}
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 sm:gap-3">
          <span className="truncate text-sm font-medium">{c.name}</span>
          <GeoTrafficBar
            sessions={traffic?.sessions ?? 0}
            pct={traffic?.pct ?? 0}
            totalSessions={totalSessions}
            showZero
          />
          <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px] font-normal text-muted-foreground">
            {provinceLabelText}
          </Badge>
          {provinceMismatch && (
            <span
              className="flex shrink-0 items-center gap-1 text-amber-700 dark:text-amber-400"
              title={`Provincia «${provinceLabelText}»: ${provincialCta}. Esta ciudad: ${current}.`}
            >
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              <span className="hidden text-[10px] font-medium sm:inline">≠ provincia</span>
            </span>
          )}
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            {c.geo_code}
          </code>
          <CtaBadge type={current} />
        </div>
        <WishlistCtaPicker value={current} onChange={onCtaChange} />
      </div>
      {(c.aliases?.length ?? 0) > 0 && (
        <div className="ml-3 space-y-1 border-l-2 border-blue-200/80 pl-3 dark:border-blue-900/60">
          {c.aliases!.map((a) => (
            <div
              key={a.geo_code}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-blue-50/40 px-2 py-1.5 text-xs dark:bg-blue-950/25"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <Badge variant="outline" className="text-[10px] font-normal">
                  variante CF
                </Badge>
                <span className="font-medium text-foreground/90">{a.name}</span>
                <code className="font-mono text-[10px] text-muted-foreground">{a.geo_code}</code>
              </div>
              <span className="text-[10px] text-muted-foreground">
                Mismo CTA que <strong>{c.name}</strong> al guardar
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function WishlistAdmin() {
  const { toast } = useToast();
  const { getAuthHeaders } = useAdminAuth();

  const [isLoading, setIsLoading]   = useState(true);
  const [isSaving, setIsSaving]     = useState(false);

  // Map: scope+geo_code → cta_type
  const [arConfig, setArConfig]         = useState<Record<string, CtaType>>({});
  const [countryConfig, setCountryConfig] = useState<Record<string, CtaType>>({});
  /** Ciudades del catálogo nacional: geo_code normalizado → CtaType. */
  const [cityCatalogConfig, setCityCatalogConfig] = useState<Record<string, CtaType>>({});
  /** Ciudades fuera del catálogo (ad-hoc, agregadas manualmente). */
  const [cityArRows, setCityArRows]   = useState<CityArRow[]>([]);
  /** Snapshot post-load / post-save — el save selectivo solo envía diffs. */
  const [baseline, setBaseline] = useState<WishlistGeoBaseline | null>(null);
  /** Filtro por nombre en el tab de ciudades. */
  const [cityFilter, setCityFilter] = useState("");
  /** Filtrar catálogo por provincia (all = todas). */
  const [cityProvinceFilter, setCityProvinceFilter] = useState<string>("all");

  /** Ocultar cumpleaños + wishlist + comunidad en el hero (BaseTemplate), vía DB + KV — sin redeploy Worker. */
  const [hideGlobalHeroCta, setHideGlobalHeroCta] = useState(false);
  const [isSavingHide, setIsSavingHide] = useState(false);

  /** Lista unificada: búsqueda y filtro por tipo de alcance (país / provincia / ciudad). */
  const [listUnifiedFilter, setListUnifiedFilter] = useState("");
  const [listScopeFilter, setListScopeFilter] = useState<UnifiedScopeFilter>("all");

  /** Tab Países: búsqueda y filtro (209 ISO2 + DEFAULT). */
  const [countryFilter, setCountryFilter] = useState("");
  const [countryListTab, setCountryListTab] = useState<CountryListFilter>("all");

  /** Tráfico AR 7d (funnel_events) — UI decide por color, no por ventanas. */
  const [geoTraffic, setGeoTraffic] = useState<GeoTrafficPayload | null>(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const trafficPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const provinceLabel = useCallback(
    (code: string) => AR_PROVINCES.find((p) => p.code === code)?.name ?? code,
    [],
  );

  /** Ciudades del catálogo cuyo CTA difiere de su provincia (debería ser 0 tras alinear / guardar). */
  const catalogProvinceMismatchCount = useMemo(
    () => countCityCatalogProvinceMismatches(arConfig, cityCatalogConfig),
    [cityCatalogConfig, arConfig],
  );

  const provinceTrafficByCode = useMemo(() => {
    const map = new Map<string, { sessions: number; pct: number }>();
    if (!geoTraffic) return map;
    for (const row of geoTraffic.provinces) {
      if (!row.province_code) continue;
      const prev = map.get(row.province_code);
      if (prev) {
        map.set(row.province_code, {
          sessions: prev.sessions + row.sessions,
          pct: prev.pct + row.pct,
        });
      } else {
        map.set(row.province_code, { sessions: row.sessions, pct: row.pct });
      }
    }
    return map;
  }, [geoTraffic]);

  const cityTrafficByCanonical = useMemo(() => {
    const map = new Map<string, { sessions: number; pct: number }>();
    if (!geoTraffic) return map;
    const total = geoTraffic.total_ar_sessions;
    for (const row of geoTraffic.cities) {
      const canon = AR_CITY_ALIAS_TO_CANONICAL.get(row.city_key) ?? row.city_key;
      const prev = map.get(canon);
      const sessions = (prev?.sessions ?? 0) + row.sessions;
      map.set(canon, {
        sessions,
        pct: total > 0 ? Math.round((sessions / total) * 1000) / 10 : 0,
      });
    }
    return map;
  }, [geoTraffic]);

  const discoveredTrafficCities = useMemo(() => {
    if (!geoTraffic) return [];
    return geoTraffic.cities
      .filter((row) => {
        const keys = wishlistCityLookupKeys(row.city);
        const inCatalog =
          AR_CITY_CATALOG_KEYS.has(row.city_key) ||
          keys.some((k) => AR_CITY_CATALOG_KEYS.has(k));
        return !inCatalog && row.sessions > 0;
      })
      .sort((a, b) => b.sessions - a.sessions);
    // sin tope: todas las ciudades reales fuera de catálogo con tráfico
  }, [geoTraffic]);

  /**
   * Inventario completo del dashboard: TODO el catálogo city_ar (con o sin tráfico)
   * + discovered con tráfico. Orden: sesiones desc, luego nombre.
   */
  const trafficRanking = useMemo(() => {
    type RankRow = {
      key: string;
      name: string;
      geo_code: string;
      provinceCode: string | null;
      sessions: number;
      pct: number;
      cta: CtaType;
      inCatalog: boolean;
      region?: string;
    };
    const rows: RankRow[] = [];
    const total = geoTraffic?.total_ar_sessions ?? 0;

    for (const c of AR_CITY_CATALOG) {
      const t = cityTrafficByCanonical.get(c.geo_code);
      const sessions = t?.sessions ?? 0;
      rows.push({
        key: `cat-${c.geo_code}`,
        name: c.name,
        geo_code: c.geo_code,
        provinceCode: c.provinceCode,
        sessions,
        pct: t?.pct ?? (total > 0 ? Math.round((sessions / total) * 1000) / 10 : 0),
        cta: cityCatalogConfig[c.geo_code] ?? "none",
        inCatalog: true,
        region: c.region,
      });
    }
    for (const row of discoveredTrafficCities) {
      rows.push({
        key: `disc-${row.city_key}`,
        name: row.city,
        geo_code: row.city_key,
        provinceCode: row.province_code,
        sessions: row.sessions,
        pct: row.pct,
        cta:
          cityArRows.find((r) => normalizeCityKey(r.geo_code) === row.city_key)?.cta_type ??
          "none",
        inCatalog: false,
      });
    }
    rows.sort((a, b) => {
      if (b.sessions !== a.sessions) return b.sessions - a.sessions;
      return a.name.localeCompare(b.name, "es");
    });
    return rows;
  }, [geoTraffic, cityTrafficByCanonical, cityCatalogConfig, cityArRows, discoveredTrafficCities]);

  const catalogCoverage = useMemo(() => {
    const withTraffic = AR_CITY_CATALOG.filter(
      (c) => (cityTrafficByCanonical.get(c.geo_code)?.sessions ?? 0) > 0,
    ).length;
    return {
      total: AR_CITY_CATALOG.length,
      withTraffic,
      zeroTraffic: AR_CITY_CATALOG.length - withTraffic,
    };
  }, [cityTrafficByCanonical]);

  const getProvinceTraffic = useCallback(
    (code: string) => provinceTrafficByCode.get(code) ?? null,
    [provinceTrafficByCode],
  );

  const getCityTraffic = useCallback(
    (geoCode: string) => cityTrafficByCanonical.get(geoCode) ?? null,
    [cityTrafficByCanonical],
  );

  const loadTraffic = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!opts?.silent) setTrafficLoading(true);
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`/api/admin/wishlist-geo-traffic?days=7`, {
          headers,
          cache: "no-store",
        });
        if (res.ok) {
          setGeoTraffic((await res.json()) as GeoTrafficPayload);
        }
      } catch {
        /* keep previous snapshot */
      } finally {
        if (!opts?.silent) setTrafficLoading(false);
      }
    },
    [getAuthHeaders],
  );

  const alignCatalogWithProvinces = useCallback(() => {
    setCityCatalogConfig((prev) => alignCityCatalogToProvinces(arConfig, prev));
    toast({
      title: "Ciudades del catálogo alineadas",
      description:
        "Cada entrada heredó el CTA de su provincia. Revisá el listado y pulsá Guardar para publicar.",
    });
  }, [arConfig, toast]);

  // No auto-alinear ciudades al editar provincia: city_ar gana en el Worker.
  // Overrides de barrio/ciudad se preservan. Usá "Alinear catálogo" si querés
  // forzar espejo provincia → todas las ciudades.

  const unifiedCtaRowsAll = useMemo((): UnifiedCtaRow[] => {
    const rows: UnifiedCtaRow[] = [];
    for (const c of INTERNATIONAL_COUNTRIES) {
      rows.push({ id: `ct-${c.code}`, bucket: "country", code: c.code, label: c.name });
    }
    for (const p of AR_PROVINCES) {
      rows.push({
        id: `pr-${p.code}`,
        bucket: "province",
        code: p.code,
        label: p.name,
        region: p.region,
      });
    }
    for (const c of AR_CITY_CATALOG) {
      rows.push({
        id: `city-${c.geo_code}`,
        bucket: "city_cat",
        canonical: c.geo_code,
        label: c.name,
        region: c.region,
        provinceCode: c.provinceCode,
      });
      for (const a of c.aliases ?? []) {
        rows.push({
          id: `alias-${c.geo_code}-${a.geo_code}`,
          bucket: "city_alias",
          canonical: c.geo_code,
          aliasCode: a.geo_code,
          label: a.name,
          parentLabel: c.name,
          region: c.region,
          provinceCode: c.provinceCode,
        });
      }
    }
    for (const r of cityArRows) {
      rows.push({ id: `adh-${r.key}`, bucket: "city_adhoc", rowKey: r.key, geo_code: r.geo_code });
    }
    return rows;
  }, [cityArRows]);

  const unifiedCtaRowsFiltered = useMemo(() => {
    const qNorm = normalizeCityKey(listUnifiedFilter.trim());
    return unifiedCtaRowsAll.filter((row) => {
      if (listScopeFilter !== "all") {
        if (listScopeFilter === "city_cat") {
          if (row.bucket !== "city_cat" && row.bucket !== "city_alias") return false;
        } else if (row.bucket !== listScopeFilter) return false;
      }
      if (!qNorm) return true;
      return normalizeCityKey(unifiedRowSearchBlob(row)).includes(qNorm);
    });
  }, [unifiedCtaRowsAll, listUnifiedFilter, listScopeFilter]);

  const cityFilterNorm = useMemo(() => normalizeCityKey(cityFilter.trim()), [cityFilter]);

  const provinceCityGroups = useMemo(
    () => buildProvinceCityGroups(AR_CITY_CATALOG, cityFilterNorm, cityProvinceFilter),
    [cityFilterNorm, cityProvinceFilter],
  );

  const cityCatalogStats = useMemo(() => {
    const barrios = AR_CITY_CATALOG.filter((c) => c.region === "Barrios CABA").length;
    const aliasRows = AR_CITY_CATALOG.reduce((n, c) => n + (c.aliases?.length ?? 0), 0);
    return {
      total: AR_CITY_CATALOG.length,
      barrios,
      aliasRows,
      persistRows: AR_CITY_CATALOG.length + aliasRows,
    };
  }, []);

  const filteredCountriesForTab = useMemo(() => {
    const q = countryFilter.trim().toLowerCase();
    return INTERNATIONAL_COUNTRIES.filter((c) => {
      if (
        countryListTab === "priority" &&
        c.code !== "DEFAULT" &&
        !PRIORITY_COUNTRIES.includes(c.code as (typeof PRIORITY_COUNTRIES)[number])
      ) {
        return false;
      }
      if (countryListTab === "configured") {
        const cur = countryConfig[c.code] ?? DEFAULT_COUNTRY_CTA;
        if (c.code === "DEFAULT") return cur !== DEFAULT_COUNTRY_CTA;
        if (cur === DEFAULT_COUNTRY_CTA) return false;
      }
      if (!q) return true;
      return `${c.code} ${c.name}`.toLowerCase().includes(q);
    });
  }, [countryFilter, countryListTab, countryConfig]);

  // ── Propagation verification — confirms a save reached the public cache ──
  type PropState =
    | { kind: "idle" }
    | {
        kind: "verifying";
        saveAt: Date;
        /** Monotonic KV counter después del POST — comparar con `/api/wishlist-config-version` (evita falsos timeouts por skew de reloj cliente/servidor). */
        targetKvVersion: number | null;
        lastSeenLm: string | null;
        lastSeenKvVersion: number | null;
        attempt: number;
      }
    | { kind: "propagated"; saveAt: Date; propagatedAt: Date; lastModified: string; matchedVersion: number | null }
    | { kind: "timeout"; saveAt: Date; lastSeenLm: string | null };
  const [propState, setPropState] = useState<PropState>({ kind: "idle" });
  const [cacheBackend, setCacheBackend] = useState<
    "upstash-kv" | "postgres" | "in-memory" | null
  >(null);

  // Primero: mismo `version` que devolvió el POST (KV). Sin depender del reloj del navegador.
  // Fallback: `updated_at` de DB ±5 s vs marca de tiempo del guardado.
  // Una sola dependency estable por ciclo — si no, cada tick reiniciaba el intervalo y el timeout de 90s.
  const propagationVerifyKey =
    propState.kind === "verifying"
      ? `${propState.saveAt.getTime()}-${propState.targetKvVersion ?? ""}`
      : "";
  useEffect(() => {
    if (propState.kind !== "verifying") return;
    const saveAtMs = propState.saveAt.getTime();
    const targetKvVersion = propState.targetKvVersion;
    const saveAtRef = propState.saveAt;

    const startedAt = Date.now();
    const id = window.setInterval(async () => {
      try {
        const r = await fetch(`/api/wishlist-config-version?_t=${Date.now()}`, {
          cache: "no-store",
        });
        const data = (await r.json()) as {
          version?: number;
          updated_at?: string | null;
        };
        const lm = data.updated_at ?? null;
        const v =
          typeof data.version === "number" && Number.isFinite(data.version)
            ? data.version
            : null;

        const matchedByKv =
          targetKvVersion !== null && v !== null && v >= targetKvVersion;

        const observedTime = lm ? new Date(lm).getTime() : 0;
        const matchedByTime =
          !matchedByKv &&
          observedTime >= saveAtMs - 5000; /* tolerancia relojes cliente vs Postgres */

        if (matchedByKv || matchedByTime) {
          window.clearInterval(id);
          setPropState({
            kind: "propagated",
            saveAt: saveAtRef,
            propagatedAt: new Date(),
            lastModified: lm ?? "",
            matchedVersion: v,
          });
          return;
        }
        if (Date.now() - startedAt > 90_000) {
          window.clearInterval(id);
          setPropState({
            kind: "timeout",
            saveAt: saveAtRef,
            lastSeenLm: lm,
          });
          return;
        }
        setPropState((s) =>
          s.kind === "verifying"
            ? {
                ...s,
                lastSeenLm: lm,
                lastSeenKvVersion: v,
                attempt: s.attempt + 1,
              }
            : s,
        );
      } catch {
        // network blip — keep trying
      }
    }, 2_000);
    return () => window.clearInterval(id);
  }, [propState.kind, propagationVerifyKey]);

  const saveHideGlobalOnly = async (value: boolean) => {
    setIsSavingHide(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/wishlist-config", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ hideGlobalHeroCta: value }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error || "Error guardando interruptor");
      }
      const saveResp = (await res.json()) as {
        cache?: { backend?: "upstash-kv" | "postgres" | "in-memory"; version?: number };
      };
      setHideGlobalHeroCta(value);
      setBaseline((prev) =>
        prev ? { ...prev, hideGlobalHeroCta: value } : prev,
      );
      if (saveResp.cache?.backend) setCacheBackend(saveResp.cache.backend);
      // DualCTA SPA (Pipeline B): soft-refetch site_settings immediately on other tabs.
      broadcastSiteSettingsUpdated();
      const saveAt = new Date();
      const targetKvVersion =
        typeof saveResp.cache?.version === "number" &&
        Number.isFinite(saveResp.cache.version)
          ? saveResp.cache.version
          : null;
      trackCtaRegionEvent(CTA_REGION_EVENTS.hideGlobalToggle, {
        hide_global_hero_cta: value,
        cache_backend: saveResp.cache?.backend ?? null,
        kv_version: targetKvVersion,
      });
      setPropState({
        kind: "verifying",
        saveAt,
        targetKvVersion,
        lastSeenLm: null,
        lastSeenKvVersion: null,
        attempt: 0,
      });
      toast({
        title: "Interruptor guardado",
        description:
          "Worker KV ~2–6 s. DualCTA SPA: broadcast al instante (otras pestañas / soft-refetch).",
      });
    } catch (err) {
      trackCtaRegionEvent(CTA_REGION_EVENTS.saveError, {
        error: err instanceof Error ? err.message : "unknown",
        context: "hide_global_toggle",
      });
      toast({
        title: "Error al guardar interruptor",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setIsSavingHide(false);
    }
  };

  // ── Carga ────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/wishlist-config", { headers });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Error cargando configuración");
      }
      const data: { entries: WishlistGeoEntry[]; hideGlobalHeroCta?: boolean } =
        await res.json();

      setHideGlobalHeroCta(Boolean(data.hideGlobalHeroCta));
      const ar: Record<string, CtaType>      = {};
      const country: Record<string, CtaType> = {};
      const catalog: Record<string, CtaType> = {};
      const cities: CityArRow[] = [];

      // Recolectar primero todas las filas city_ar del DB en un map indexado
      // por geo_code normalizado. Después de un pass las consolido por
      // canónico — si CUALQUIER variante (canónica o alias) tiene un
      // cta_type ≠ "none", ese gana sobre las que están en "none".
      const dbCityRows = new Map<string, CtaType>();

      for (const e of data.entries) {
        // Solo Exclusivo Mux (community) o none — resto deshabilitado.
        const cta = sanitizeCta(e.cta_type);
        if (e.scope === "province_ar") ar[e.geo_code] = cta;
        else if (e.scope === "country") country[e.geo_code] = cta;
        else if (e.scope === "city_ar" && e.geo_code?.trim()) {
          dbCityRows.set(normalizeCityKey(e.geo_code), cta);
        }
      }

      // Dashboard completo: TODAS las provincias y países del catálogo (aunque no haya fila DB)
      for (const p of AR_PROVINCES) {
        if (ar[p.code] === undefined) ar[p.code] = DEFAULT_AR_CTA;
      }
      for (const c of INTERNATIONAL_COUNTRIES) {
        if (country[c.code] === undefined) country[c.code] = DEFAULT_COUNTRY_CTA;
      }

      // Catálogo: hidratar desde DB city_ar (barrio/ciudad gana al Worker).
      // Sin fila DB → hereda provincia. Variantes CF heredan el canónico al guardar.
      const consumed = new Set<string>();
      for (const c of AR_CITY_CATALOG) {
        const allKeys = [c.geo_code, ...(c.aliases ?? []).map((a) => a.geo_code)];
        for (const k of allKeys) consumed.add(normalizeCityKey(k));
      }
      Object.assign(catalog, hydrateCityCatalogFromDb(ar, dbCityRows, normalizeCityKey));

      // Cualquier fila city_ar del DB que NO matcheó ningún canónico ni alias
      // se considera ad-hoc (admin la creó manualmente fuera del catálogo).
      let adhocIdx = 0;
      for (const [norm, cta] of dbCityRows.entries()) {
        if (!consumed.has(norm)) {
          cities.push({
            key: `db-${adhocIdx++}-${norm}`,
            geo_code: norm,
            cta_type: cta,
          });
        }
      }

      setArConfig(ar);
      setCountryConfig(country);
      setCityCatalogConfig(catalog);
      setCityArRows(cities);
      setBaseline(
        snapshotWishlistBaseline({
          arConfig: ar,
          countryConfig: country,
          cityCatalogConfig: catalog,
          cityArRows: cities,
          hideGlobalHeroCta: Boolean(data.hideGlobalHeroCta),
          normalizeKey: normalizeCityKey,
        }),
      );
    } catch (err) {
      toast({
        title: "Error al cargar",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAuthHeaders]); // toast stable — excluded to avoid re-fetch loop

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadTraffic();
  }, [loadTraffic]);

  // Refresh silencioso del mapa de calor (7d).
  useEffect(() => {
    trafficPollRef.current = setInterval(() => {
      void loadTraffic({ silent: true });
    }, 60_000);
    return () => {
      if (trafficPollRef.current) {
        clearInterval(trafficPollRef.current);
        trafficPollRef.current = null;
      }
    };
  }, [loadTraffic]);

  useEffect(() => {
    trackCtaRegionEvent(CTA_REGION_EVENTS.pageView, {
      brand: BRAND_SOURCE_KEY,
      path: "/admin/wishlist-config",
    });
  }, []);

  // Track KV propagation outcomes for PostHog ops dashboards
  useEffect(() => {
    if (propState.kind === "propagated") {
      trackCtaRegionEvent(CTA_REGION_EVENTS.propagationOk, {
        latency_ms:
          propState.propagatedAt.getTime() - propState.saveAt.getTime(),
        matched_version: propState.matchedVersion,
        cache_backend: cacheBackend,
      });
    } else if (propState.kind === "timeout") {
      trackCtaRegionEvent(CTA_REGION_EVENTS.propagationTimeout, {
        last_seen_lm: propState.lastSeenLm,
        cache_backend: cacheBackend,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire on kind transitions
  }, [propState.kind]);

  // ── Dirty diff (solo lo modificado vs baseline) ───────────────────────────

  const dirtyDiff = useMemo(() => {
    const sanitizeMap = (m: Record<string, CtaType>) => {
      const out: Record<string, CtaType> = {};
      for (const [k, v] of Object.entries(m)) out[k] = sanitizeCta(v);
      return out;
    };
    return buildWishlistGeoDirtyDiff({
      baseline,
      arConfig: sanitizeMap(arConfig),
      countryConfig: sanitizeMap(countryConfig),
      cityCatalogConfig: sanitizeMap(cityCatalogConfig),
      cityArRows: cityArRows.map((r) => ({
        ...r,
        cta_type: sanitizeCta(r.cta_type),
      })),
      hideGlobalHeroCta,
      normalizeKey: normalizeCityKey,
    });
  }, [baseline, arConfig, countryConfig, cityCatalogConfig, cityArRows, hideGlobalHeroCta]);

  // ── Guardar (selectivo: solo diffs; full = syncCityAr replace) ────────────

  const save = async (mode: "dirty" | "full" = "dirty") => {
    setIsSaving(true);
    try {
      const headers = await getAuthHeaders();

      const sanitizeMap = (m: Record<string, CtaType>) => {
        const out: Record<string, CtaType> = {};
        for (const [k, v] of Object.entries(m)) out[k] = sanitizeCta(v);
        return out;
      };
      const safeAr = sanitizeMap(arConfig);
      const safeCountry = sanitizeMap(countryConfig);
      const safeCatalog = sanitizeMap(cityCatalogConfig);
      const safeCityRows = cityArRows.map((r) => ({
        ...r,
        cta_type: sanitizeCta(r.cta_type),
      }));

      let entries: WishlistGeoEntry[];
      let deleteCityAr: string[] = [];
      let syncCityAr = false;
      let includeHide = false;

      if (mode === "full") {
        entries = buildWishlistGeoPersistEntries({
          arConfig: safeAr,
          countryConfig: safeCountry,
          cityCatalogConfig: safeCatalog,
          cityArRows: safeCityRows,
        }) as WishlistGeoEntry[];
        syncCityAr = true;
        includeHide = true;
      } else {
        const diff = buildWishlistGeoDirtyDiff({
          baseline,
          arConfig: safeAr,
          countryConfig: safeCountry,
          cityCatalogConfig: safeCatalog,
          cityArRows: safeCityRows,
          hideGlobalHeroCta,
          normalizeKey: normalizeCityKey,
        });
        if (diff.total === 0) {
          toast({
            title: "Nada que guardar",
            description: "No hay cambios respecto al último load/save.",
          });
          return;
        }
        entries = diff.entries as WishlistGeoEntry[];
        deleteCityAr = diff.deleteCityAr;
        includeHide = diff.summary.hideGlobalChanged;
        syncCityAr = false;
      }

      console.group("🔍 WishlistAdmin - Save selectivo");
      console.log("mode", mode, "entries", entries.length, "deleteCityAr", deleteCityAr);
      console.log("scopes", {
        province: entries.filter((e) => e.scope === "province_ar").length,
        country: entries.filter((e) => e.scope === "country").length,
        city: entries.filter((e) => e.scope === "city_ar").length,
      });
      console.groupEnd();

      const body: Record<string, unknown> = {
        entries,
        syncCityAr,
      };
      if (deleteCityAr.length > 0) body.deleteCityAr = deleteCityAr;
      if (includeHide) body.hideGlobalHeroCta = hideGlobalHeroCta;

      const res = await fetch("/api/admin/wishlist-config", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Error guardando");
      }

      const saveResp = (await res.json().catch(() => ({}))) as {
        saved?: number;
        partial?: boolean;
        cache?: {
          backend?: "upstash-kv" | "postgres" | "in-memory";
          version?: number;
        };
      };
      if (saveResp.cache?.backend) setCacheBackend(saveResp.cache.backend);

      const saveAt = new Date();
      const targetKvVersion =
        typeof saveResp.cache?.version === "number" &&
        Number.isFinite(saveResp.cache.version)
          ? saveResp.cache.version
          : null;

      // Nuevo baseline = estado actual (lo guardado ya está en DB)
      setBaseline(
        snapshotWishlistBaseline({
          arConfig: safeAr,
          countryConfig: safeCountry,
          cityCatalogConfig: safeCatalog,
          cityArRows: safeCityRows,
          hideGlobalHeroCta,
          normalizeKey: normalizeCityKey,
        }),
      );

      trackCtaRegionEvent(CTA_REGION_EVENTS.save, {
        entries_total: entries.length,
        save_mode: mode,
        partial: mode === "dirty",
        deleted_city_ar: deleteCityAr.length,
        hide_global_hero_cta: includeHide ? hideGlobalHeroCta : null,
        cache_backend: saveResp.cache?.backend ?? null,
        kv_version: targetKvVersion,
        sync_city_ar: syncCityAr,
      });

      setPropState({
        kind: "verifying",
        saveAt,
        targetKvVersion,
        lastSeenLm: null,
        lastSeenKvVersion: null,
        attempt: 0,
      });

      toast({
        title:
          mode === "dirty"
            ? `Guardado selectivo (${saveResp.saved ?? entries.length} filas)`
            : "Guardado full (replace city_ar)",
        description:
          mode === "dirty"
            ? "Solo cambios pendientes. Verificando propagación al Worker…"
            : "Catálogo completo reescrito. Verificando propagación…",
      });
    } catch (err) {
      trackCtaRegionEvent(CTA_REGION_EVENTS.saveError, {
        error: err instanceof Error ? err.message : "unknown",
      });
      toast({
        title: "Error al guardar",
        description: err instanceof Error ? err.message : "Error desconocido",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Hooks must run before any early return (rules-of-hooks / CI eslint).
  const arSummary = useMemo(
    () => ({
      none: AR_PROVINCES.filter((p) => sanitizeCta(arConfig[p.code] ?? DEFAULT_AR_CTA) === "none")
        .length,
      community: AR_PROVINCES.filter(
        (p) => sanitizeCta(arConfig[p.code] ?? DEFAULT_AR_CTA) === "community",
      ).length,
    }),
    [arConfig],
  );

  const cityCtaSummary = useMemo(() => {
    let exclusive = 0;
    let none = 0;
    let other = 0;
    let barriosExclusive = 0;
    let barriosTotal = 0;
    for (const c of AR_CITY_CATALOG) {
      const t = cityCatalogConfig[c.geo_code] ?? "none";
      if (c.region === "Barrios CABA") {
        barriosTotal += 1;
        if (t === "community") barriosExclusive += 1;
      }
      if (t === "community") exclusive += 1;
      else if (t === "none") none += 1;
      else other += 1;
    }
    for (const r of cityArRows) {
      const k = normalizeCityKey(r.geo_code);
      if (AR_CITY_CATALOG.some((c) => normalizeCityKey(c.geo_code) === k)) continue;
      if (r.cta_type === "community") exclusive += 1;
      else if (r.cta_type === "none") none += 1;
      else other += 1;
    }
    return { exclusive, none, other, barriosExclusive, barriosTotal, catalog: AR_CITY_CATALOG.length };
  }, [cityArRows, cityCatalogConfig]);

  // Agrupar provincias AR por región
  const regionMap = useMemo(() => {
    const map: Record<string, typeof AR_PROVINCES> = {};
    for (const p of AR_PROVINCES) {
      if (!map[p.region]) map[p.region] = [];
      map[p.region].push(p);
    }
    return map;
  }, []);

  /**
   * Cambiar CTA de una provincia y cascade a TODAS las city_ar del catálogo de esa provincia.
   * Necesario: el Worker prioriza city_ar; si quedan en none el botón Exclusivo no aparece.
   */
  const setProvinceCtaWithCityCascade = useCallback(
    (provinceCode: string, cta: CtaType, opts?: { toastTitle?: string }) => {
      const safe = sanitizeCta(cta);
      setArConfig((prev) => ({ ...prev, [provinceCode]: safe }));
      setCityCatalogConfig((prev) =>
        cascadeProvinceCtaToCityCatalog(prev, provinceCode, safe),
      );
      toast({
        title:
          opts?.toastTitle ??
          `${provinceCode} → ${safe === "community" ? "Exclusivo Mux" : "Sin botón"} (borrador)`,
        description:
          "Provincia + ciudades/barrios del catálogo alineados. Worker: city_ar gana si cf.city matchea. Guardá → live (~2–5 s).",
      });
    },
    [toast],
  );

  /**
   * CABA completo gestionable: province_ar CABA + city_ar capital + todos los barrios.
   * Sin esto, cf.city=Buenos Aires / Palermo con city_ar=none tapa a porteños aunque el interior esté en community.
   */
  const setAllCabaManagedCta = useCallback(
    (cta: CtaType) => {
      setProvinceCtaWithCityCascade("CABA", cta, {
        toastTitle:
          cta === "community"
            ? "CABA completo → Exclusivo Mux (borrador)"
            : "CABA completo → Sin botón (borrador)",
      });
    },
    [setProvinceCtaWithCityCascade],
  );

  /** Provincias + catálogo city_ar + ad-hoc city_ar a un CTA (borrador). */
  const setAllProvincesAndCitiesCta = useCallback((cta: CtaType) => {
    const all: Record<string, CtaType> = {};
    AR_PROVINCES.forEach((p) => {
      all[p.code] = cta;
    });
    setArConfig(all);
    setCityCatalogConfig(alignCityCatalogToProvinces(all));
    // Ad-hoc (ciudades reales fuera del catálogo / mojibake DB) también impactan city_ar
    setCityArRows((prev) => prev.map((r) => ({ ...r, cta_type: cta })));
    toast({
      title:
        cta === "community"
          ? "AR completo → Exclusivo (borrador)"
          : cta === "none"
            ? "AR completo → Sin botón (borrador)"
            : "AR completo actualizado (borrador)",
      description:
        "24 provincias + catálogo city_ar + filas ad-hoc. Destino hero: /invitacion-exclusive. Guardá para publicar (~2–5 s).",
    });
  }, [toast]);

  /** Provincias AR en none (bloquean hero Exclusivo salvo city_ar más específica). */
  const arProvincesBlocked = useMemo(
    () =>
      AR_PROVINCES.filter(
        (p) => sanitizeCta(arConfig[p.code] ?? DEFAULT_AR_CTA) === "none",
      ),
    [arConfig],
  );

  /** Ciudades CABA del catálogo aún en none (incl. capital CF). */
  const cabaCitiesBlocked = useMemo(() => {
    return AR_CITY_CATALOG.filter(
      (c) =>
        c.provinceCode === "CABA" &&
        sanitizeCta(cityCatalogConfig[c.geo_code] ?? "none") === "none",
    );
  }, [cityCatalogConfig]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-28">
      <AdminPageHeader
        description={
          <>
            Gestioná dónde se muestra el botón <strong>Exclusivo</strong> →{" "}
            <code className="text-[10px]">/invitacion-exclusive</code>. Inventario completo en las
            pestañas. Save <strong>solo de lo modificado</strong>. Live Worker ~2–5 s.
          </>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to="/admin/live-funnel">
                <BarChart3 className="mr-1.5 h-4 w-4" />
                Live funnel
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a href="/invitacion-exclusive" target="_blank" rel="noreferrer">
                <Sparkles className="mr-1.5 h-4 w-4 text-purple-500" />
                Preview exclusive
              </a>
            </Button>
            <Button
              onClick={() => void save("dirty")}
              disabled={isSaving || dirtyDiff.total === 0}
            >
              {isSaving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>
              ) : (
                <><Save className="mr-2 h-4 w-4" />
                  Guardar {dirtyDiff.total > 0 ? `(${dirtyDiff.total})` : ""}
                </>
              )}
            </Button>
          </div>
        }
      />

      {/* Sticky: única barra de acciones de gestión */}
      <div className="sticky top-0 z-30 -mx-1 border-b border-border/70 bg-background/95 px-2 py-2.5 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-background/90">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              size="sm"
              className="h-9"
              onClick={() => void save("dirty")}
              disabled={isSaving || dirtyDiff.total === 0}
            >
              {isSaving ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-4 w-4" />
              )}
              Guardar {dirtyDiff.total > 0 ? `${dirtyDiff.total} cambios` : "selectivo"}
            </Button>
            {dirtyDiff.total > 0 ? (
              <Badge className="h-9 gap-1 bg-purple-600 px-2.5 text-[11px]">
                Dirty: {dirtyDiff.summary.provinces.length}p ·{" "}
                {dirtyDiff.summary.cities.length}c · {dirtyDiff.summary.countries.length}país
                {dirtyDiff.summary.deletedCities.length
                  ? ` · −${dirtyDiff.summary.deletedCities.length}`
                  : ""}
              </Badge>
            ) : (
              <Badge variant="secondary" className="h-9 gap-1 text-[11px]">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                Sin cambios pendientes
              </Badge>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setAllProvincesAndCitiesCta("community")}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5 text-purple-500" />
              AR → Exclusivo
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setAllCabaManagedCta("community")}
            >
              <MapPin className="mr-1.5 h-3.5 w-3.5" />
              CABA → Exclusivo
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span
              className={
                hideGlobalHeroCta
                  ? "inline-flex items-center gap-1.5 rounded-md border border-amber-500/70 bg-amber-50 px-2 py-1 dark:bg-amber-950/40"
                  : "inline-flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-1"
              }
            >
              <Switch
                id="hide-global-hero-cta"
                checked={hideGlobalHeroCta}
                disabled={isSavingHide}
                onCheckedChange={(v) => void saveHideGlobalOnly(v)}
                className="scale-90"
              />
              <Label
                htmlFor="hide-global-hero-cta"
                className={
                  hideGlobalHeroCta
                    ? "cursor-pointer font-semibold leading-none text-amber-900 dark:text-amber-100"
                    : "cursor-pointer font-medium leading-none"
                }
                title={
                  hideGlobalHeroCta
                    ? "Kill switch ON: el Worker fuerza hideCta y anula city_ar/province_ar (Exclusivo no se muestra aunque esté asignado)."
                    : "Apagado: se aplican los CTAs por ciudad/provincia/país."
                }
              >
                {hideGlobalHeroCta
                  ? "⚠ Hero CTAs ocultos (anula geo)"
                  : "Hero CTAs ON"}
              </Label>
            </span>
            <Badge variant="outline" className="tabular-nums">
              {arSummary.community}/{AR_PROVINCES.length} prov exclusivo
            </Badge>
            <Badge variant="outline" className="tabular-nums">
              {cityCtaSummary.exclusive}/{cityCtaSummary.catalog} city exclusivo
            </Badge>
            <Badge variant="outline" className="tabular-nums">
              {unifiedCtaRowsAll.length} filas
            </Badge>
          </div>
        </div>
      </div>

      {/* Alerts solo cuando hay algo que validar (save / bloqueos) */}
      {arProvincesBlocked.length > 0 && (
        <Alert className="border-amber-300/80 bg-amber-50/70 py-2 dark:border-amber-800 dark:bg-amber-950/30">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertTitle className="text-sm">
            {arProvincesBlocked.length} provincia(s) sin botón:{" "}
            {arProvincesBlocked.map((p) => p.code).join(", ")}
          </AlertTitle>
          <AlertDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {arProvincesBlocked.some((p) => p.code === "CABA") && (
              <span>CABA bloquea gran parte del tráfico AR si city_ar también está en none.</span>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7"
              onClick={() => setAllCabaManagedCta("community")}
            >
              CABA → Exclusivo
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {propState.kind === "verifying" && (
        <Alert className="border-amber-300 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-950/30">
          <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
          <AlertTitle className="text-amber-700 dark:text-amber-300">
            Verificando propagación al endpoint público…
            {cacheBackend && (
              <span className="ml-2 rounded bg-amber-200/60 px-1.5 py-0.5 text-[10px] font-mono text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
                vía{" "}
                {cacheBackend === "upstash-kv"
                  ? "Upstash Redis REST"
                  : cacheBackend === "postgres"
                    ? "Postgres kv_store"
                    : "memoria local"}
              </span>
            )}
          </AlertTitle>
          <AlertDescription className="mt-1 text-xs text-amber-700/80 dark:text-amber-300/80">
            Save:&nbsp;<strong className="tabular-nums">{propState.saveAt.toLocaleTimeString("es-AR")}</strong>
            {propState.targetKvVersion !== null && (
              <>
                &nbsp;· Versión KV esperada:&nbsp;
                <strong className="tabular-nums">{propState.targetKvVersion}</strong>
              </>
            )}
            &nbsp;· Intentos:&nbsp;<strong className="tabular-nums">{propState.attempt}</strong>
            {propState.lastSeenKvVersion !== null && (
              <>
                &nbsp;· Versión vista en API:&nbsp;
                <code className="font-mono tabular-nums">{propState.lastSeenKvVersion}</code>
              </>
            )}
            {propState.lastSeenLm && (
              <>
                &nbsp;· Último <span className="font-mono">updated_at</span> DB:&nbsp;
                <code className="font-mono">{new Date(propState.lastSeenLm).toLocaleTimeString("es-AR")}</code>
              </>
            )}
            <p className="mt-1">
              Polling <code className="font-mono">/api/wishlist-config-version</code> cada 2 s
              {propState.targetKvVersion !== null
                ? " hasta que la versión pública alcance la del guardado (sin depender del reloj del navegador)."
                : "."}
              {cacheBackend === "upstash-kv" || cacheBackend === "postgres"
                ? " Con KV activo la propagación entre réplicas de Railway es <1 s; el Worker chequea versión cada ~2.5 s → live típico ~2–5 s."
                : " Sin Upstash/Postgres KV configurado el cache es por instancia — distintas réplicas de Railway pueden ver versiones distintas hasta que cada una refresque."}
            </p>
          </AlertDescription>
        </Alert>
      )}
      {propState.kind === "propagated" && (
        <Alert className="border-emerald-300 bg-emerald-50/60 dark:border-emerald-800 dark:bg-emerald-950/30">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <AlertTitle className="text-emerald-700 dark:text-emerald-400">
            Propagado en {Math.round((propState.propagatedAt.getTime() - propState.saveAt.getTime()) / 1000)}&nbsp;s
          </AlertTitle>
          <AlertDescription className="mt-1 text-xs text-emerald-700/80 dark:text-emerald-400/80">
            La nueva configuración ya es visible en el endpoint público
            {propState.matchedVersion !== null && (
              <>
                {" "}
                (versión API <span className="font-mono tabular-nums">{propState.matchedVersion}</span>).
              </>
            )}
            . El Worker detecta el bump de versión en ~2.5–5 s (L1 mid-path); solo en outage del version endpoint cae al techo de 60 s.
            <button
              onClick={() => setPropState({ kind: "idle" })}
              className="ml-2 underline hover:text-emerald-900 dark:hover:text-emerald-300"
            >
              Cerrar
            </button>
          </AlertDescription>
        </Alert>
      )}
      {propState.kind === "timeout" && (
        <Alert className="border-red-300 bg-red-50/60 dark:border-red-800 dark:bg-red-950/30">
          <Info className="h-4 w-4 text-red-600" />
          <AlertTitle className="text-red-700 dark:text-red-400">
            Propagación no confirmada tras 90 s
          </AlertTitle>
          <AlertDescription className="mt-1 text-xs text-red-700/80 dark:text-red-400/80">
            La DB tiene la nueva config (lo guardaste a las {propState.saveAt.toLocaleTimeString("es-AR")}), pero el endpoint público sigue devolviendo una versión vieja. Posibles causas: cache de Cloudflare edge ≥60 s, problema de red, o el cron que purga el cache no corrió.
            <button
              onClick={() => setPropState({ kind: "idle" })}
              className="ml-2 underline hover:text-red-900 dark:hover:text-red-300"
            >
              Cerrar
            </button>
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="lista-unificada" className="space-y-3">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="lista-unificada" className="gap-1.5">
            <ListOrdered className="h-4 w-4" />
            Lista ({unifiedCtaRowsAll.length})
          </TabsTrigger>
          <TabsTrigger value="argentina" className="gap-1.5">
            <MapPin className="h-4 w-4" />
            Provincias ({AR_PROVINCES.length})
          </TabsTrigger>
          <TabsTrigger value="cities-ar" className="gap-1.5">
            <Building2 className="h-4 w-4" />
            Ciudades ({AR_CITY_CATALOG.length}
            {cityArRows.length ? `+${cityArRows.length}` : ""})
          </TabsTrigger>
          <TabsTrigger value="countries" className="gap-1.5">
            <Globe className="h-4 w-4" />
            Países ({INTERNATIONAL_COUNTRIES.length})
          </TabsTrigger>
          <TabsTrigger value="validar" className="gap-1.5">
            <Zap className="h-4 w-4" />
            Validar
          </TabsTrigger>
        </TabsList>

        {/* ── Lista unificada (país · provincia · ciudad) ───────────────────────── */}
        <TabsContent value="lista-unificada" className="mt-0 space-y-3">
          <p className="text-sm text-muted-foreground">
            Tabla maestra: todo lo gestionable. Prioridad Worker:{" "}
            <strong>ciudad &gt; provincia &gt; país</strong>. Editá → Guardar selectivo en la barra sticky.
          </p>

          <Card className="border-border/80 shadow-sm">
            <CardContent className="space-y-3 pt-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <Input
                  placeholder="Buscar por nombre, código, región o provincia…"
                  value={listUnifiedFilter}
                  onChange={(ev) => setListUnifiedFilter(ev.target.value)}
                  className="max-w-md"
                />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">Mostrar</span>
                  <Select
                    value={listScopeFilter}
                    onValueChange={(v) => setListScopeFilter(v as UnifiedScopeFilter)}
                  >
                    <SelectTrigger className="w-[200px]" aria-label="Filtrar por tipo de ubicación">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los alcances</SelectItem>
                      <SelectItem value="country">Solo países</SelectItem>
                      <SelectItem value="province">Solo provincias AR</SelectItem>
                      <SelectItem value="city_cat">Ciudades + variantes CF</SelectItem>
                      <SelectItem value="city_adhoc">Solo ciudades ad-hoc</SelectItem>
                    </SelectContent>
                  </Select>
                  <Badge variant="outline" className="tabular-nums">
                    {unifiedCtaRowsFiltered.length} / {unifiedCtaRowsAll.length}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-xs text-muted-foreground">
            Inventario completo:{" "}
            <strong className="text-foreground tabular-nums">{unifiedCtaRowsAll.length}</strong> filas
            (países + {AR_PROVINCES.length} provincias + catálogo city_ar + aliases + ad-hoc). Filtro
            actual:{" "}
            <strong className="tabular-nums text-foreground">{unifiedCtaRowsFiltered.length}</strong>.
          </p>
          <div className="max-h-[min(85vh,960px)] overflow-auto rounded-md border border-border/70">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-background shadow-sm">
                <TableRow>
                  <TableHead className="w-[112px] min-w-[100px]">Alcance</TableHead>
                  <TableHead>Ubicación</TableHead>
                  <TableHead className="hidden w-[220px] min-w-[180px] md:table-cell">Código geo</TableHead>
                  <TableHead className="text-right">CTA</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unifiedCtaRowsFiltered.map((row) => {
                  let current: CtaType;
                  let geoCell: ReactNode;
                  let alcanceBadge: ReactNode;
                  let ubicacionTitle: string;
                  let geoMobileInline: ReactNode | null = null;
                  let provinceMismatch = false;

                  switch (row.bucket) {
                    case "country":
                      ubicacionTitle = row.label;
                      current = countryConfig[row.code] ?? DEFAULT_COUNTRY_CTA;
                      geoMobileInline = <span className="font-mono">{row.code}</span>;
                      geoCell = (
                        <>
                          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{row.code}</code>
                          {row.code === "DEFAULT" && (
                            <span className="mt-1 block text-xs text-muted-foreground">
                              Fallback internacional cuando el país del visitante no tiene fila específica.
                            </span>
                          )}
                        </>
                      );
                      alcanceBadge = (
                        <Badge variant="secondary" className="text-[10px] font-semibold uppercase tracking-wide">
                          País
                        </Badge>
                      );
                      break;
                    case "province":
                      ubicacionTitle = row.label;
                      current = arConfig[row.code] ?? DEFAULT_AR_CTA;
                      geoMobileInline = (
                        <>
                          <span className="font-mono">{row.code}</span>
                          <span className="text-muted-foreground"> · {row.region}</span>
                        </>
                      );
                      geoCell = (
                        <>
                          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{row.code}</code>
                          <span className="mt-0.5 block text-xs text-muted-foreground">{row.region}</span>
                        </>
                      );
                      alcanceBadge = (
                        <Badge variant="secondary" className="text-[10px] font-semibold uppercase tracking-wide bg-blue-500/15 text-blue-900 dark:bg-blue-500/25 dark:text-blue-200">
                          Provincia AR
                        </Badge>
                      );
                      break;
                    case "city_cat": {
                      ubicacionTitle = row.label;
                      current = cityCatalogConfig[row.canonical] ?? "none";
                      const provCta = arConfig[row.provinceCode] ?? DEFAULT_AR_CTA;
                      provinceMismatch = current !== provCta;
                      geoMobileInline = (
                        <>
                          <span className="font-mono">{row.canonical}</span>
                          <span className="text-muted-foreground">
                            {" "}
                            · {row.region} · {provinceLabel(row.provinceCode)}
                          </span>
                        </>
                      );
                      geoCell = (
                        <>
                          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{row.canonical}</code>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            {row.region} · {provinceLabel(row.provinceCode)}
                          </span>
                        </>
                      );
                      alcanceBadge = (
                        <Badge variant="secondary" className="text-[10px] font-semibold uppercase tracking-wide bg-violet-500/15 text-violet-900 dark:bg-violet-500/25 dark:text-violet-200">
                          Ciudad
                        </Badge>
                      );
                      break;
                    }
                    case "city_alias": {
                      ubicacionTitle = row.label;
                      current = cityCatalogConfig[row.canonical] ?? "none";
                      const provCtaAlias = arConfig[row.provinceCode] ?? DEFAULT_AR_CTA;
                      provinceMismatch = current !== provCtaAlias;
                      geoMobileInline = (
                        <>
                          <span className="font-mono">{row.aliasCode}</span>
                          <span className="text-muted-foreground">
                            {" "}
                            → {row.parentLabel} ({row.canonical})
                          </span>
                        </>
                      );
                      geoCell = (
                        <>
                          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{row.aliasCode}</code>
                          <span className="mt-0.5 block text-[11px] text-muted-foreground">
                            Variante de <strong>{row.parentLabel}</strong> · {row.region} ·{" "}
                            {provinceLabel(row.provinceCode)}
                          </span>
                          <span className="mt-0.5 block text-[10px] text-blue-700/90 dark:text-blue-300/90">
                            Mismo CTA que {row.parentLabel} al guardar
                          </span>
                        </>
                      );
                      alcanceBadge = (
                        <Badge variant="secondary" className="text-[10px] font-semibold uppercase tracking-wide bg-blue-500/15 text-blue-900 dark:bg-blue-500/25 dark:text-blue-200">
                          Variante CF
                        </Badge>
                      );
                      break;
                    }
                    case "city_adhoc":
                      ubicacionTitle = row.geo_code;
                      current = cityArRows.find((r) => r.key === row.rowKey)?.cta_type ?? "none";
                      geoMobileInline = <span className="font-mono">{row.geo_code}</span>;
                      geoCell = (
                        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{row.geo_code}</code>
                      );
                      alcanceBadge = (
                        <Badge variant="secondary" className="text-[10px] font-semibold uppercase tracking-wide bg-amber-500/15 text-amber-900 dark:bg-amber-500/25 dark:text-amber-200">
                          Ciudad · otra
                        </Badge>
                      );
                      break;
                  }

                  return (
                    <TableRow
                      key={row.id}
                      className={provinceMismatch ? "bg-amber-50/50 dark:bg-amber-950/20" : undefined}
                    >
                      <TableCell className="align-top">{alcanceBadge}</TableCell>
                      <TableCell className="max-w-[min(320px,40vw)] align-top">
                        <span className="font-medium">{ubicacionTitle}</span>
                        {geoMobileInline && (
                          <div className="mt-1 text-[11px] leading-snug text-muted-foreground md:hidden">
                            {geoMobileInline}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="hidden align-top text-muted-foreground md:table-cell">
                        {geoCell}
                      </TableCell>
                      <TableCell className="space-y-1 text-right align-top">
                        {provinceMismatch && (
                          <div
                            className="flex items-center justify-end gap-1 text-[10px] text-amber-700 dark:text-amber-400"
                            title={`Esta ciudad gana sobre la provincia en el Worker si cf.city coincide. Difiere del CTA provincial actual.`}
                          >
                            <AlertTriangle className="h-3 w-3 shrink-0" />
                            ≠ provincia
                          </div>
                        )}
                        <div className="flex flex-col items-end gap-2 sm:flex-row sm:justify-end sm:gap-3">
                          <CtaBadge type={current} />
                          <WishlistCtaPicker
                            value={current}
                            triggerClassName="w-[min(200px,calc(100vw-12rem))]"
                            onChange={(v: CtaType) => {
                              switch (row.bucket) {
                                case "country":
                                  setCountryConfig((prev) => ({ ...prev, [row.code]: v }));
                                  break;
                                case "province":
                                  // Cascade city_ar of this province so Worker doesn't keep city=none
                                  setProvinceCtaWithCityCascade(row.code, v);
                                  break;
                                case "city_cat":
                                case "city_alias":
                                  setCityCatalogConfig((prev) => ({
                                    ...prev,
                                    [row.canonical]: v,
                                  }));
                                  break;
                                case "city_adhoc":
                                  setCityArRows((prev) =>
                                    prev.map((r) => (r.key === row.rowKey ? { ...r, cta_type: v } : r)),
                                  );
                                  break;
                              }
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {unifiedCtaRowsFiltered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="py-12 text-center text-sm text-muted-foreground">
                      Ningún resultado con este filtro. Probá borrar la búsqueda o cambiar “Mostrar”.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ── Tab Argentina ─────────────────────────────────────────────────── */}
        <TabsContent value="argentina" className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">
            Configurá el botón CTA que aparece en la home para cada provincia argentina.
            Las provincias que no estén en la base de datos muestran <strong>"Sin botón"</strong> por defecto.
          </p>

          {geoTraffic && geoTraffic.total_ar_sessions > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200/70 bg-sky-50/40 px-3 py-2 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300">
              <BarChart3 className="h-3.5 w-3.5 shrink-0" />
              <span>
                Tráfico AR últimos <strong>{geoTraffic.days} días</strong>:{" "}
                <strong>{geoTraffic.total_ar_sessions.toLocaleString("es-AR")}</strong> sesiones.
                La barra y el % indican la participación de cada provincia — útil para priorizar CTAs.
              </span>
            </div>
          )}

          {Object.entries(regionMap).map(([region, provinces]) => (
            <Card key={region} className="border-border/80 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {region}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {[...provinces]
                  .sort(
                    (a, b) =>
                      (getProvinceTraffic(b.code)?.sessions ?? 0) -
                      (getProvinceTraffic(a.code)?.sessions ?? 0),
                  )
                  .map((p) => {
                  const current: CtaType = arConfig[p.code] ?? DEFAULT_AR_CTA;
                  const traffic = getProvinceTraffic(p.code);
                  return (
                    <div
                      key={p.code}
                      className="flex items-center justify-between gap-4 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <span className="truncate text-sm font-medium">{p.name}</span>
                        {traffic && (
                          <GeoTrafficBar
                            sessions={traffic.sessions}
                            pct={traffic.pct}
                            totalSessions={geoTraffic?.total_ar_sessions ?? 0}
                          />
                        )}
                        <CtaBadge type={current} />
                      </div>
                      <WishlistCtaPicker
                        value={current}
                        onChange={(v) => setProvinceCtaWithCityCascade(p.code, v)}
                      />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}

          {/* Acciones rápidas */}
          <Card className="border-dashed border-border/60 bg-muted/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Acciones rápidas</CardTitle>
              <CardDescription>Aplica un CTA a todas las provincias a la vez</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAllProvincesAndCitiesCta("none")}
              >
                <Ban className="mr-1.5 h-3.5 w-3.5" /> Quitar todos (+ ciudades)
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAllProvincesAndCitiesCta("community")}
              >
                <Sparkles className="mr-1.5 h-3.5 w-3.5 text-purple-500" /> Todos Exclusivo Mux (+ ciudades)
              </Button>
              <p className="w-full text-[11px] text-muted-foreground">
                Solo <strong>Exclusivo Mux</strong> o <strong>Sin botón</strong>. Comunidad/wishlist/dual están deshabilitados.
                Barrios: pestaña Ciudades (<code className="text-[10px]">city_ar</code> gana al Worker).
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab Ciudades AR (prioridad sobre provincia en el Worker) ───────── */}
        <TabsContent value="cities-ar" className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">
            Catálogo completo: <strong>{AR_CITY_CATALOG.length} city_ar</strong> (barrios CABA + GBA + interior).
            El Worker usa <code className="rounded bg-muted px-1 text-xs">cf.city</code> →{" "}
            <code className="rounded bg-muted px-1 text-xs">city_ar</code> gana a provincia.
            El <strong>punto de color</strong> te dice al toque si vale prender Exclusivo Mux.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setAllCabaManagedCta("community")}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5 text-purple-500" />
              CABA completo → Exclusivo Mux
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => setAllCabaManagedCta("none")}
            >
              <Ban className="mr-1.5 h-3.5 w-3.5" />
              Quitar CTA CABA (prov + capital + barrios)
            </Button>
            <Badge variant="secondary" className="tabular-nums text-[11px]">
              Barrios exclusivo: {cityCtaSummary.barriosExclusive}/{cityCtaSummary.barriosTotal}
            </Badge>
            <Badge variant="outline" className="tabular-nums text-[11px]">
              Catálogo {catalogCoverage.total} · con tráfico {catalogCoverage.withTraffic} · sin{" "}
              {catalogCoverage.zeroTraffic}
            </Badge>
            <Link
              to="/admin/live-funnel"
              className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            >
              Live funnel AR →
            </Link>
          </div>

          {/* Mapa de calor: color = ¿vale Exclusivo? (sin ventanas 1h/24h) */}
          <Card className="border-sky-200/80 bg-sky-50/30 dark:border-sky-900/50 dark:bg-sky-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                <BarChart3 className="h-4 w-4 text-sky-600" />
                Mapa de calor · ¿dónde hay gente?
                {trafficLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
              </CardTitle>
              <CardDescription className="text-xs">
                Mirá el <strong>punto de color</strong> — no hace falta comparar horas.
                Verde = vale prender Exclusivo Mux. Gris = nadie. Actualiza solo en background.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-[11px]">
                {(
                  [
                    ["hot", "Muy alto · prioridad"],
                    ["high", "Alto · vale Exclusivo"],
                    ["mid", "Medio · evaluar"],
                    ["low", "Poco"],
                    ["none", "Sin tráfico"],
                  ] as const
                ).map(([level, text]) => (
                  <span key={level} className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <span
                      className={`inline-block h-3 w-3 rounded-full ring-2 ${TRAFFIC_HEAT_META[level].ring} ${TRAFFIC_HEAT_META[level].dot}`}
                    />
                    {text}
                  </span>
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-7 px-2"
                  disabled={trafficLoading}
                  onClick={() => void loadTraffic()}
                >
                  <RefreshCw className={`mr-1 h-3.5 w-3.5 ${trafficLoading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
              </div>

              <p className="mb-2 text-xs text-muted-foreground">
                Mostrando <strong className="tabular-nums text-foreground">{trafficRanking.length}</strong>{" "}
                puntos (catálogo completo + discovered con tráfico). Click = toggle Exclusivo (borrador).
              </p>
              {trafficRanking.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Catálogo vacío o aún cargando. Las pestañas Lista / Ciudades tienen el inventario.
                </p>
              ) : (
                <div className="grid max-h-[min(70vh,720px)] gap-1.5 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
                  {trafficRanking.map((row) => {
                    const total = geoTraffic?.total_ar_sessions ?? 0;
                    const heat = trafficHeatLevel(row.sessions, total);
                    const meta = TRAFFIC_HEAT_META[heat];
                    return (
                      <button
                        key={row.key}
                        type="button"
                        onClick={() => {
                          if (!row.inCatalog) {
                            setCityArRows((prev) => {
                              const k = normalizeCityKey(row.geo_code);
                              if (prev.some((r) => normalizeCityKey(r.geo_code) === k)) {
                                return prev.map((r) =>
                                  normalizeCityKey(r.geo_code) === k
                                    ? {
                                        ...r,
                                        cta_type:
                                          r.cta_type === "community" ? "none" : "community",
                                      }
                                    : r,
                                );
                              }
                              return [
                                ...prev,
                                {
                                  key:
                                    typeof crypto !== "undefined" && crypto.randomUUID
                                      ? crypto.randomUUID()
                                      : `disc-${k}`,
                                  geo_code: row.geo_code,
                                  cta_type: "community",
                                },
                              ];
                            });
                            toast({
                              title: `${row.name} → ad-hoc Exclusivo (borrador)`,
                              description: "Guardá para publicar.",
                            });
                            return;
                          }
                          const next: CtaType = row.cta === "community" ? "none" : "community";
                          setCityCatalogConfig((prev) => ({ ...prev, [row.geo_code]: next }));
                          toast({
                            title:
                              next === "community"
                                ? `${row.name} → Exclusivo Mux (borrador)`
                                : `${row.name} → Sin botón (borrador)`,
                            description: "Guardá para publicar (~2–5 s).",
                          });
                        }}
                        className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition-colors hover:bg-muted/40 ${
                          heat === "hot" || heat === "high"
                            ? "border-emerald-300/70 bg-emerald-50/40 dark:border-emerald-800 dark:bg-emerald-950/30"
                            : heat === "none"
                              ? "border-border/40 bg-background/50"
                              : "border-border/50 bg-background/70"
                        }`}
                        title={`${meta.hint} · click para toggle Exclusivo`}
                      >
                        <TrafficHeatIcon sessions={row.sessions} totalSessions={total} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-xs font-semibold leading-tight">{row.name}</div>
                          <div className="truncate text-[10px] text-muted-foreground">
                            {meta.label}
                            {row.provinceCode ? ` · ${provinceLabel(row.provinceCode)}` : ""}
                            {row.cta === "community" ? " · ya Exclusivo" : ""}
                          </div>
                        </div>
                        <span className="shrink-0 text-[10px] font-bold tabular-nums text-foreground/70">
                          {row.sessions.toLocaleString("es-AR")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {catalogProvinceMismatchCount > 0 && (
            <Alert className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/25">
              <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <AlertTitle className="text-sm text-amber-800 dark:text-amber-300">
                {catalogProvinceMismatchCount} ciudad(es) del catálogo no coinciden con su provincia
              </AlertTitle>
              <AlertDescription className="mt-1 flex flex-col gap-2 text-xs text-amber-800/85 dark:text-amber-400/90 sm:flex-row sm:items-center sm:justify-between">
                <span>
                  En producción el visitante recibe el <strong>CTA de la ciudad</strong> si <code className="rounded bg-amber-100/80 px-1 font-mono dark:bg-amber-900/50">cf.city</code>{" "}
                  matchea. Los selectores provinciales sólo aplican si no hay fila de ciudad más específica.
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-amber-300 bg-white/80 hover:bg-amber-100/50 dark:border-amber-700 dark:bg-amber-950/60"
                  onClick={alignCatalogWithProvinces}
                >
                  <MapPin className="mr-1.5 h-3.5 w-3.5" />
                  Alinear catálogo con provincias
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {catalogProvinceMismatchCount === 0 && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-emerald-200/70 bg-emerald-50/40 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                Todas las ciudades del catálogo están alineadas con su provincia según configuración provincial.
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 text-emerald-800 hover:bg-emerald-100/70 dark:text-emerald-300"
                onClick={alignCatalogWithProvinces}
              >
                Refrescar desde provincias
              </Button>
            </div>
          )}

          {/* Resumen del catálogo */}
          {(() => {
            const total = cityCatalogStats.total;
            const active = AR_CITY_CATALOG.filter(
              (c) => (cityCatalogConfig[c.geo_code] ?? "none") !== "none",
            ).length;
            const adhocActive = cityArRows.filter(
              (r) => r.cta_type !== "none" && r.geo_code.trim(),
            ).length;
            return (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm">
                  <Building2 className="h-3.5 w-3.5" />
                  Catálogo: <span className="font-semibold">{active}</span>/{total} activadas
                </Badge>
                <Badge variant="outline" className="gap-1 px-2.5 py-1 text-xs">
                  {cityCatalogStats.barrios} barrios CABA
                </Badge>
                <Badge variant="outline" className="gap-1 px-2.5 py-1 text-xs">
                  {cityCatalogStats.aliasRows} variantes CF
                </Badge>
                <Badge variant="outline" className="gap-1 px-2.5 py-1 text-xs tabular-nums">
                  {cityCatalogStats.persistRows} filas al guardar
                </Badge>
                {adhocActive > 0 && (
                  <Badge variant="outline" className="gap-1.5 px-3 py-1.5 text-sm">
                    <Plus className="h-3.5 w-3.5" />
                    {adhocActive} ad-hoc activas
                  </Badge>
                )}
              </div>
            );
          })()}

          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-sky-200/70 bg-sky-50/40 px-3 py-2 text-xs text-sky-900 dark:border-sky-900 dark:bg-sky-950/30 dark:text-sky-300">
            <BarChart3 className="h-3.5 w-3.5 shrink-0" />
            <span>
              Catálogo completo ({AR_CITY_CATALOG.length} city_ar). El{" "}
              <strong>punto de color</strong> al lado de cada ciudad indica si hay gente (verde =
              prender Exclusivo; gris = no).
              {discoveredTrafficCities.length > 0 && (
                <>
                  {" "}
                  + <strong>{discoveredTrafficCities.length}</strong> fuera de catálogo con tráfico.
                </>
              )}
            </span>
          </div>

          {/* Filtros */}
          <Card className="border-dashed border-border/60 bg-muted/20">
            <CardContent className="flex flex-col gap-3 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
              <Input
                placeholder="Buscar ciudad, barrio, variante CF o provincia…"
                value={cityFilter}
                onChange={(ev) => setCityFilter(ev.target.value)}
                className="max-w-md"
              />
              <Select value={cityProvinceFilter} onValueChange={setCityProvinceFilter}>
                <SelectTrigger className="w-[min(280px,100%)]" aria-label="Filtrar por provincia">
                  <SelectValue placeholder="Provincia" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las provincias ({AR_PROVINCES.length})</SelectItem>
                  {AR_PROVINCES.map((p) => (
                    <SelectItem key={p.code} value={p.code}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Badge variant="outline" className="tabular-nums">
                {provinceCityGroups.reduce((n, g) => n + g.totalCities, 0)} visibles
              </Badge>
            </CardContent>
          </Card>

          {/* Catálogo agrupado por provincia → región */}
          {provinceCityGroups.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Ninguna ciudad coincide con el filtro. Probá otra provincia o borrá la búsqueda.
            </p>
          ) : (
            provinceCityGroups.map((group: ProvinceCityGroup) => {
              const provinceActive = group.regions.reduce(
                (n, r) =>
                  n +
                  r.cities.filter((c) => (cityCatalogConfig[c.geo_code] ?? "none") !== "none").length,
                0,
              );
              const defaultOpen =
                cityProvinceFilter !== "all" ||
                group.provinceCode === "CABA" ||
                group.totalCities <= 24;
              return (
                <Collapsible key={group.provinceCode} defaultOpen={defaultOpen}>
                  <Card className="border-border/80 shadow-sm">
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer pb-2 transition-colors hover:bg-muted/30">
                        <div className="flex items-center justify-between gap-2">
                          <CardTitle className="flex items-center gap-2 text-base">
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform [[data-state=open]_&]:rotate-180" />
                            {group.provinceName}
                            <span className="text-xs font-normal text-muted-foreground">
                              ({provinceActive}/{group.totalCities} activas · {group.macroRegion})
                            </span>
                          </CardTitle>
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="space-y-4 pt-0">
                        {group.regions.map((regionGroup) => {
                          const citiesSorted = [...regionGroup.cities].sort(
                            (a, b) =>
                              (getCityTraffic(b.geo_code)?.sessions ?? 0) -
                              (getCityTraffic(a.geo_code)?.sessions ?? 0),
                          );
                          const regionActive = regionGroup.cities.filter(
                            (c) => (cityCatalogConfig[c.geo_code] ?? "none") !== "none",
                          ).length;
                          return (
                            <div key={`${group.provinceCode}-${regionGroup.region}`} className="space-y-2">
                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/40 pb-2">
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  {regionGroup.region}
                                  <span className="ml-2 font-normal normal-case text-muted-foreground/70">
                                    ({regionActive}/{regionGroup.cities.length})
                                  </span>
                                </h4>
                                <div className="flex flex-wrap gap-1">
                                  {(
                                    [
                                      ["none", Ban, "Quitar", ""],
                                      ["community", Sparkles, "Exclusivo Mux", "text-purple-500"],
                                    ] as const
                                  ).map(([cta, Icon, label, iconClass]) => (
                                    <Button
                                      key={cta}
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 px-2 text-xs"
                                      onClick={() =>
                                        setCityCatalogConfig((prev) => {
                                          const next = { ...prev };
                                          regionGroup.cities.forEach((c) => {
                                            next[c.geo_code] = cta;
                                          });
                                          return next;
                                        })
                                      }
                                    >
                                      <Icon className={`mr-1 h-3 w-3 ${iconClass}`} />
                                      {label}
                                    </Button>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                {citiesSorted.map((c) => (
                                  <CatalogCityEditorRow
                                    key={c.geo_code}
                                    c={c}
                                    current={cityCatalogConfig[c.geo_code] ?? "none"}
                                    provincialCta={arConfig[c.provinceCode] ?? DEFAULT_AR_CTA}
                                    traffic={getCityTraffic(c.geo_code)}
                                    totalSessions={geoTraffic?.total_ar_sessions ?? 0}
                                    provinceLabelText={provinceLabel(c.provinceCode)}
                                    onCtaChange={(v) =>
                                      setCityCatalogConfig((prev) => ({ ...prev, [c.geo_code]: v }))
                                    }
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              );
            })
          )}

          {/* ── Ciudades con tráfico no catalogadas ─────────────────────────── */}
          {discoveredTrafficCities.length > 0 && (
            <Card className="border-sky-200/80 bg-sky-50/20 shadow-sm dark:border-sky-900 dark:bg-sky-950/20">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <BarChart3 className="h-4 w-4 text-sky-600 dark:text-sky-400" />
                  Ciudades con tráfico (no en catálogo)
                </CardTitle>
                <CardDescription>
                  Detectadas en analytics los últimos {geoTraffic?.days ?? 7} días. Podés agregarlas como ad-hoc
                  o ampliar el catálogo en código si son recurrentes.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {discoveredTrafficCities.map((row) => (
                  <div
                    key={`disc-${row.city_key}-${row.province_code ?? ""}`}
                    className="flex items-center justify-between gap-4 rounded-lg border border-border/50 bg-background/60 px-3 py-2.5"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <span className="truncate text-sm font-medium">{row.city}</span>
                      {row.province_code && (
                        <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px] font-normal text-muted-foreground">
                          {provinceLabel(row.province_code)}
                        </Badge>
                      )}
                      <GeoTrafficBar
                        sessions={row.sessions}
                        pct={row.pct}
                        totalSessions={geoTraffic?.total_ar_sessions ?? 0}
                      />
                      <code className="hidden rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
                        {row.city_key}
                      </code>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="shrink-0"
                      onClick={() => {
                        const exists = cityArRows.some(
                          (r) => normalizeCityKey(r.geo_code) === row.city_key,
                        );
                        if (exists) return;
                        setCityArRows((prev) => [
                          ...prev,
                          {
                            key:
                              typeof crypto !== "undefined" && crypto.randomUUID
                                ? crypto.randomUUID()
                                : `traffic-${Date.now()}`,
                            geo_code: row.city_key,
                            cta_type: arConfig[row.province_code ?? ""] ?? DEFAULT_AR_CTA,
                          },
                        ]);
                        toast({
                          title: "Ciudad agregada",
                          description: `${row.city} → pestaña ad-hoc. Guardá para publicar.`,
                        });
                      }}
                    >
                      <Plus className="mr-1.5 h-3.5 w-3.5" />
                      Agregar CTA
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* ── Otras ciudades (ad-hoc, fuera del catálogo) ───────────────── */}
          <Card className="border-border/80 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">Otras ciudades (ad-hoc)</CardTitle>
                  <CardDescription>
                    Para ciudades fuera del catálogo nacional (ej. variantes de ISP no mapeadas).
                    El Worker normaliza el nombre al comparar contra <code className="rounded bg-muted px-1 text-xs">cf.city</code>.
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() =>
                    setCityArRows((prev) => [
                      ...prev,
                      {
                        key: typeof crypto !== "undefined" && crypto.randomUUID
                          ? crypto.randomUUID()
                          : `new-${Date.now()}`,
                        geo_code: "",
                        cta_type: "community",
                      },
                    ])
                  }
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  Agregar otra ciudad
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {cityArRows.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No hay ciudades ad-hoc. Si la ciudad que necesitás no está en el catálogo de arriba, agregala acá.
                </p>
              ) : (
                cityArRows.map((row) => (
                  <div
                    key={row.key}
                    className="flex flex-col gap-2 rounded-lg border border-border/50 bg-muted/20 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                      <Input
                        placeholder="Nombre ciudad (ej. Rosario)"
                        value={row.geo_code}
                        onChange={(ev) =>
                          setCityArRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key ? { ...r, geo_code: ev.target.value } : r,
                            ),
                          )
                        }
                        className="sm:max-w-xs"
                      />
                      <WishlistCtaPicker
                        value={row.cta_type}
                        triggerClassName="w-full sm:w-[220px]"
                        onChange={(v) =>
                          setCityArRows((prev) =>
                            prev.map((r) =>
                              r.key === row.key ? { ...r, cta_type: v } : r,
                            ),
                          )
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 self-end sm:self-center"
                      onClick={() =>
                        setCityArRows((prev) => prev.filter((r) => r.key !== row.key))
                      }
                      aria-label="Quitar ciudad"
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab Países ──────────────────────────────────────────────────────── */}
        <TabsContent value="countries" className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">
            {INTERNATIONAL_COUNTRIES.length} entradas (DEFAULT + {INTERNATIONAL_COUNTRIES.length - 1} países).
            <strong> DEFAULT</strong> aplica a cualquier ISO2 sin fila explícita. Impacta el botón hero de
            BaseTemplate (fallback internacional). Los overlays Mux en `/` los controla{" "}
            <Link to="/admin/country-templates" className="font-medium underline underline-offset-2">
              country-templates
            </Link>
            .
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <Input
              placeholder="Buscar país por nombre o código ISO…"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="max-w-md"
            />
            <Tabs
              value={countryListTab}
              onValueChange={(v) => setCountryListTab(v as CountryListFilter)}
            >
              <TabsList>
                <TabsTrigger value="all">Todos ({INTERNATIONAL_COUNTRIES.length})</TabsTrigger>
                <TabsTrigger value="priority">Prioritarios</TabsTrigger>
                <TabsTrigger value="configured">Con override</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <Card className="border-border/80 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">
                {filteredCountriesForTab.length} país{filteredCountriesForTab.length === 1 ? "" : "es"}
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[min(85vh,900px)] space-y-2 overflow-y-auto pt-0">
              {filteredCountriesForTab.map((c) => {
                const current: CtaType = countryConfig[c.code] ?? DEFAULT_COUNTRY_CTA;
                return (
                  <div
                    key={c.code}
                    className="flex items-center justify-between gap-4 rounded-lg border border-border/50 bg-muted/20 px-3 py-2.5"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="truncate text-sm font-medium">
                        {c.code !== "DEFAULT" && (
                          <span className="mr-1.5 rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                            {c.code}
                          </span>
                        )}
                        {c.name}
                      </span>
                      <CtaBadge type={current} />
                    </div>
                    <WishlistCtaPicker
                      value={current}
                      onChange={(v) =>
                        setCountryConfig((prev) => ({ ...prev, [c.code]: v }))
                      }
                    />
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Alert className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30">
            <Globe className="h-4 w-4 text-blue-600" />
            <AlertTitle className="text-blue-700 dark:text-blue-400">Complemento: plantillas por país</AlertTitle>
            <AlertDescription className="text-blue-600/80 dark:text-blue-400/80">
              Para elegir plantilla Mux, video, precio VIP o canal gratis por país usá{" "}
              <Link to="/admin/country-templates" className="font-medium underline underline-offset-2">
                /admin/country-templates
              </Link>
              . Acá solo controlás qué botón hero se muestra (o si se oculta con &quot;Sin botón&quot;).
            </AlertDescription>
          </Alert>
        </TabsContent>

        {/* ── Validar: checklist + GeoSim + chips provincia ───────────────────── */}
        <TabsContent value="validar" className="mt-0 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Checklist de impacto</CardTitle>
              <CardDescription className="text-xs">
                Worker (init home): <code className="text-[10px]">city_ar</code> (none = hard hide)
                &gt; <code className="text-[10px]">province_ar</code> si ≠ none &gt;{" "}
                <strong>país AR = Exclusivo nacional</strong> (community). Province en «Sin botón»
                hereda país AR. Destino:{" "}
                <code className="text-[10px]">/invitacion-exclusive</code> desde el primer paint.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="rounded-lg border border-border/60 p-3">
                <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                  Inventario
                </div>
                <ul className="space-y-1 text-xs">
                  <li>
                    {AR_PROVINCES.length} provincias · {arSummary.community} exclusivo ·{" "}
                    {arSummary.none} sin botón
                  </li>
                  <li>
                    {cityCtaSummary.catalog} city catálogo · {cityCtaSummary.exclusive} exclusivo ·{" "}
                    {cityCatalogStats.aliasRows} aliases
                  </li>
                  <li>{cityArRows.length} ad-hoc DB · {INTERNATIONAL_COUNTRIES.length} países</li>
                  <li className="tabular-nums font-medium">
                    Total filas lista: {unifiedCtaRowsAll.length}
                  </li>
                </ul>
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                  Save & live
                </div>
                <ul className="space-y-1 text-xs">
                  <li>
                    Dirty actual:{" "}
                    <strong className="tabular-nums">{dirtyDiff.total}</strong> (solo eso se
                    POST-ea)
                  </li>
                  <li>KV bump → Worker version check ~2.5 s → live típico 2–5 s</li>
                  <li>
                    Kill switch hero:{" "}
                    <strong>{hideGlobalHeroCta ? "OCULTO (global)" : "off"}</strong>
                  </li>
                  <li>
                    <Link to="/admin/worker-flags" className="underline underline-offset-2">
                      Worker flags →
                    </Link>{" "}
                    ·{" "}
                    <Link to="/admin/live-funnel" className="underline underline-offset-2">
                      Live funnel →
                    </Link>
                  </li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {cabaCitiesBlocked.length > 0 && (
            <Alert className="border-amber-300/80">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-sm">
                {cabaCitiesBlocked.length} city_ar CABA en sin botón
              </AlertTitle>
              <AlertDescription className="text-xs">
                {cabaCitiesBlocked.map((c) => c.geo_code).join(", ")}. Usá{" "}
                <strong>CABA → Exclusivo</strong> en la barra sticky.
              </AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MapPin className="h-4 w-4" />
                Toggle rápido 24 provincias
              </CardTitle>
              <CardDescription className="text-xs">
                Click = cascade a city_ar del catálogo de esa provincia (borrador).
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-1.5">
              {AR_PROVINCES.map((p) => {
                const on = sanitizeCta(arConfig[p.code] ?? DEFAULT_AR_CTA) === "community";
                return (
                  <button
                    key={p.code}
                    type="button"
                    title={`${p.name}`}
                    onClick={() =>
                      setProvinceCtaWithCityCascade(p.code, on ? "none" : "community")
                    }
                    className={`rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors ${
                      on
                        ? "border-purple-300/80 bg-purple-100/80 text-purple-900 dark:border-purple-700 dark:bg-purple-950/50 dark:text-purple-100"
                        : "border-border/60 bg-background text-muted-foreground hover:bg-muted/50"
                    }`}
                  >
                    {p.code}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          <Card className="border-violet-200/80 dark:border-violet-900/40">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <Globe className="h-4 w-4 text-violet-600" />
                Validar como visitante (GeoSim)
              </CardTitle>
              <CardDescription className="text-xs">
                Edge real (template + CTA). Toolbar:{" "}
                <a href="/?geo_sim_boot=1" className="underline underline-offset-2">
                  /?geo_sim_boot=1
                </a>
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {GEO_SIM_SCENARIO_PRESETS.filter((p) => p.simulateVpn === "clean").map((p) => (
                <GeoSimCountryPreviewButton
                  key={p.id}
                  country={p.country}
                  province={p.province}
                  city={p.city}
                  state={p.state}
                  source={`wishlist_admin_validar_${p.id}`}
                  size="sm"
                  className="h-8"
                  label={p.label}
                  title={p.intent}
                />
              ))}
            </CardContent>
          </Card>

          <Alert className="border-green-200 bg-green-50/40 dark:border-green-900/40 dark:bg-green-950/20">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-sm">Pipeline</AlertTitle>
            <AlertDescription className="text-xs leading-relaxed">
              Si <strong>país AR = Exclusivo</strong>, el botón aparece en el <strong>init</strong> de
              la home AR (incluso CABA con province none), salvo city_ar barrio en «Sin botón» o kill
              switch. Pipeline: Admin → KV → Worker inject <code className="text-[10px]">showCommunity</code>{" "}
              → BaseTemplate → <code className="text-[10px]">/invitacion-exclusive</code>.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>

      {/* Guardar al pie — selectivo + full opcional */}
      <div className="flex flex-col gap-3 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-muted-foreground">
          {dirtyDiff.total === 0 ? (
            <span>Sin cambios pendientes. El Worker usa la última versión publicada.</span>
          ) : (
            <span>
              <strong className="text-foreground">{dirtyDiff.total}</strong> cambio(s) listos:{" "}
              {dirtyDiff.summary.provinces.length
                ? `prov ${dirtyDiff.summary.provinces.slice(0, 6).join(", ")}${dirtyDiff.summary.provinces.length > 6 ? "…" : ""} · `
                : ""}
              {dirtyDiff.summary.cities.length
                ? `${dirtyDiff.summary.cities.length} city_ar · `
                : ""}
              {dirtyDiff.summary.countries.length
                ? `${dirtyDiff.summary.countries.length} país · `
                : ""}
              destino real: <code className="text-[10px]">/invitacion-exclusive</code>
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSaving}
            onClick={() => void save("full")}
            title="Reescribe todo city_ar (scripts / recovery). Preferí Guardar selectivo."
          >
            Guardar todo (full)
          </Button>
          <Button
            onClick={() => void save("dirty")}
            disabled={isSaving || dirtyDiff.total === 0}
            size="lg"
          >
            {isSaving ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Guardando...</>
            ) : (
              <><Save className="mr-2 h-4 w-4" />
                Guardar {dirtyDiff.total > 0 ? `${dirtyDiff.total} seleccionados` : "cambios"}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
